(() => {
  'use strict';

  const SECTION_CONFIGS = [
    {
      selector: '.section-text-with-image',
      primarySelector: '.twi__primary-img img',
      secondarySelector: '.twi__secondary-img img',
      activeClass: 'is-scroll-fx-active',
    },
    {
      selector: '.section-collapsible-content',
      primarySelector: '.cc__image-main img',
      secondarySelector: '.cc__image-secondary img',
      activeClass: 'is-scroll-fx-active',
    },
  ];
  const SELECTOR = SECTION_CONFIGS.map((config) => config.selector).join(', ');
  const controllers = new Map();
  const DESKTOP_MQ = '(min-width: 700px)';

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    return;
  }

  const getSectionConfig = (section) => {
    return SECTION_CONFIGS.find((config) => section.matches(config.selector)) || null;
  };

  class TextWithImageScrollFx {
    constructor(section, config) {
      this.section = section;
      this.config = config;
      this.primary = section.querySelector(config.primarySelector);
      this.secondary = section.querySelector(config.secondarySelector);
      this.viewportMatch = window.matchMedia(DESKTOP_MQ);
      this.active = false;
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.update = this.update.bind(this);
    }

    init() {
      if (!this.primary || !this.secondary) {
        return false;
      }

      this.section.classList.add(this.config.activeClass);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.active = entries.some((entry) => entry.isIntersecting);
            if (this.active) this.requestTick();
          },
          { root: null, threshold: 0, rootMargin: '160px 0px' }
        );
        this.io.observe(this.section);
      } else {
        this.active = true;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);
      this.viewportMatch.addEventListener('change', this.onViewportChange);
      this.requestTick();
      return true;
    }

    destroy() {
      if (this.io) this.io.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.viewportMatch.removeEventListener('change', this.onViewportChange);

      this.section.classList.remove(this.config.activeClass);
      this.resetTransforms();
    }

    onScroll() {
      if (!this.active) return;
      this.requestTick();
    }

    onResize() {
      this.requestTick();
    }

    onViewportChange() {
      this.requestTick();
    }

    requestTick() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(this.update);
    }

    resetTransforms() {
      if (this.primary) this.primary.style.transform = '';
      if (this.secondary) this.secondary.style.transform = '';
    }

    update() {
      this.ticking = false;
      if (!this.primary || !this.secondary) return;

      if (!this.viewportMatch.matches) {
        this.resetTransforms();
        return;
      }

      const rect = this.section.getBoundingClientRect();
      const viewportMid = window.innerHeight * 0.5;
      const sectionMid = rect.top + rect.height * 0.5;
      const distance = sectionMid - viewportMid;
      const normalized = Math.max(-1, Math.min(1, distance / (window.innerHeight * 0.9)));

      // Very subtle premium opposing drift.
      const primaryY = normalized * -28;
      const secondaryY = normalized * 30;

      this.primary.style.transform = `translate3d(0, ${primaryY.toFixed(2)}px, 0)`;
      this.secondary.style.transform = `translate3d(0, ${secondaryY.toFixed(2)}px, 0)`;
    }
  }

  const initSection = (section) => {
    if (!section || controllers.has(section)) return;
    const config = getSectionConfig(section);
    if (!config) return;
    const controller = new TextWithImageScrollFx(section, config);
    if (controller.init()) controllers.set(section, controller);
  };

  const destroySection = (section) => {
    const controller = controllers.get(section);
    if (!controller) return;
    controller.destroy();
    controllers.delete(section);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(initSection);
  };

  const destroyInScope = (scope) => {
    if (!scope) return;
    scope.querySelectorAll(SELECTOR).forEach(destroySection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRoot = event.target.closest('.shopify-section') || event.target;
    if (!sectionRoot) return;
    destroyInScope(sectionRoot);
    initAll(sectionRoot);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const sectionRoot = event.target.closest('.shopify-section') || event.target;
    destroyInScope(sectionRoot);
  });
})();
